import { useQuery } from "@tanstack/react-query";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { PageHeader } from "../../components/PageHeader";
import { AppLink } from "../board/cells";
import { useAction } from "../lib/actions";
import { entityHref, entityLabel } from "../lib/entity";
import { fmtWhen } from "../lib/time";
import { ActionError, ClientMdButtons, Empty, QueryView } from "../lib/ui";
import { flagParam, useUrlState } from "../lib/url";

type Note = Schemas["NotificationOut"];
type Ref = Schemas["NotificationRef"];

const refLabel = (r: Ref) => entityLabel(r.entity, r.entity_id) || (r.job_id != null ? `job #${r.job_id}` : "");
const refHref = (r: Ref) => entityHref(r.entity, r.entity_id) ?? (r.job_id != null ? entityHref("job", r.job_id) : null);

/** "×3 · first … · last …" for aggregated rows, the single time otherwise. */
export function noteWhen(n: Note): string {
  const count = n.count ?? 1;
  if (count <= 1) return fmtWhen(n.last_at ?? n.t);
  return `×${count} · first ${fmtWhen(n.first_at ?? n.t)} · last ${fmtWhen(n.last_at ?? n.t)}`;
}

export function notificationsMarkdown(rows: Note[], unreadOnly: boolean, unreadCount?: number): string {
  const lines = [`# Notifications${unreadOnly ? " (unread)" : ""}`, "",
    `${rows.length} notifications${unreadCount != null ? `, ${unreadCount} unread in total` : ""}`, "",
    "| # | count | first | last | level | title | entity | sample | read | body |",
    "|---|---|---|---|---|---|---|---|---|---|"];
  const esc = (s: string) => s.replace(/\|/g, "\\|").replace(/\s*\n\s*/g, " ");
  for (const n of rows) {
    const sample = (n.sample ?? []).map(refLabel).filter(Boolean).join(", ");
    lines.push(`| ${n.id} | ${n.count ?? 1} | ${fmtWhen(n.first_at ?? n.t)} | ${fmtWhen(n.last_at ?? n.t)} | ${n.level} | ${esc(n.title)} | ${entityLabel(n.entity, n.entity_id)} | ${esc(sample)} | ${n.read ? "yes" : "no"} | ${esc(n.body ?? "")} |`);
  }
  return `${lines.join("\n")}\n`;
}

function Sample({ n }: { n: Note }) {
  const refs = (n.sample ?? []).filter((r) => refHref(r));
  if ((n.count ?? 1) <= 1 || refs.length === 0) return null;
  return (
    <p className="ws7-inline" style={{ margin: "4px 0", flexWrap: "wrap" }} data-testid={`notification-sample-${n.id}`}>
      <span className="ws7-muted">Affected ({refs.length}{(n.count ?? 1) > refs.length ? ` of ${n.count}` : ""}):</span>
      {refs.map((r) => (
        <AppLink key={`${r.entity}-${r.entity_id}-${r.job_id}`} href={refHref(r) ?? ""}>{refLabel(r)}</AppLink>
      ))}
    </p>
  );
}

export function NotificationsView() {
  const announce = useAnnounce();
  const { sp, set } = useUrlState();
  const unreadOnly = flagParam(sp.get("unread"));
  const list = useQuery({
    queryKey: [...qk.notifications, { unread: unreadOnly, page: true }],
    queryFn: () => unwrap(api.GET("/api/notifications", { params: { query: { unread: unreadOnly, limit: 200 } } })),
  });
  const markRead = useAction(
    (n: Note) => unwrap(api.POST("/api/notifications/{nid}/read", { params: { path: { nid: n.id } } })),
    { label: (n) => `Mark notification #${n.id} read`, success: (_r, n) => `Marked notification #${n.id} read`, invalidate: [qk.notifications] },
  );
  const markAll = useAction(
    () => unwrap(api.POST("/api/notifications/read-all")),
    { label: "Mark all read", success: () => "Marked all notifications read", invalidate: [qk.notifications] },
  );
  const rows = list.data?.items ?? [];
  const unreadCount = list.data?.unread_count ?? 0;
  return (
    <div className="ws7-page" data-testid="notifications-page">
      <PageHeader title="Notifications">
        <ClientMdButtons filename="notifications.md"
          markdown={list.data ? notificationsMarkdown(list.data.items, unreadOnly, list.data.unread_count) : null} />
      </PageHeader>
      <div className="ws7-toolbar">
        <label className="ws7-check">
          <input type="checkbox" checked={unreadOnly} data-testid="notifications-unread"
            onChange={(e) => {
              set({ unread: e.target.checked });
              announce(e.target.checked ? "Showing unread notifications" : "Showing all notifications");
            }} />
          Unread only
        </label>
        <button type="button" data-testid="notifications-read-all" disabled={markAll.pending} onClick={() => void markAll.run()}>
          Mark all read
        </button>
        <span className="ws7-muted" data-testid="notifications-count">{rows.length} shown, {unreadCount} unread</span>
      </div>
      <ActionError error={markRead.error ?? markAll.error} />
      <QueryView q={list} what="notifications">
        {(page) =>
          page.items.length === 0 ? (
            <Empty>{unreadOnly ? "No unread notifications." : "No notifications."}</Empty>
          ) : (
            <ul className="ws7-notes" aria-label="Notifications">
              {page.items.map((n) => {
                const href = entityHref(n.entity, n.entity_id);
                const count = n.count ?? 1;
                return (
                  <li key={n.id} className={`ws7-note level-${n.level}${n.read ? "" : " unread"}`} data-testid={`notification-${n.id}`}>
                    <div className="ws7-row" style={{ justifyContent: "space-between" }}>
                      <h3>
                        <span className={`pill ws7-tone-${n.level === "error" ? "bad" : n.level === "warn" ? "warn" : "info"}`}>{n.level}</span>{" "}
                        {count > 1 && <span className="pill" data-testid={`notification-count-${n.id}`} aria-label={`${count} times`}>×{count}</span>}{" "}
                        {n.title}
                      </h3>
                      <span className="ws7-inline">
                        <span className="ws7-muted" data-testid={`notification-when-${n.id}`}>{noteWhen(n)} · {n.read ? "read" : "unread"}</span>
                        {!n.read && (
                          <button type="button" data-testid={`notification-read-${n.id}`} aria-label={`Mark read: ${n.title}`}
                            disabled={markRead.pending} onClick={() => void markRead.run(n)}>
                            Mark read
                          </button>
                        )}
                      </span>
                    </div>
                    {n.body && <p style={{ margin: "4px 0", whiteSpace: "pre-wrap" }}>{n.body}</p>}
                    <Sample n={n} />
                    {href && <AppLink href={href}>Open {entityLabel(n.entity, n.entity_id)}</AppLink>}
                  </li>
                );
              })}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}
